import React, {
  Component,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { Sankey, Tooltip } from "recharts";
import _, { filter, flatten } from "lodash";
import DemoSankeyLink from "./DemoSankeyLink";
import DemoSankeyNode from "./DemoSankeyNode";
import {
  getFlowOfFunds,
  getFlowOfFundsTagLevel,
} from "../../../../docuclipper/api";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import AgTable from "../AnalyzeTransactionsTable/AgTable";
import { datefilterParamsYYYYMMDD } from "../AnalyzeTransactionsTable";
import { useTransactions } from "../AnalyzeTransactionsTable/hooks";
import { Button, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AgGridReact } from "ag-grid-react";
import { fetchMatchedTransactions } from "../../../../redux/reducers/MatchedTransactions";
// import SupersetDash from "../Categorize/SupersetPieChart.tsx.disabled";
import { useIsAdmin } from "src/utils/auth";
import { sankey, sankeyLinkHorizontal } from "d3-sankey";
import * as d3 from "d3";
import { SizeMe } from "react-sizeme";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { IDatasource } from "ag-grid-community";
import { TableFilterConfig } from "src/docuclipper/DocuclipperTypes";
import { fetchTls } from "src/redux/reducers/TransactionManager";
import { useHasQbo } from "src/utils/utils";
import ExternalFilter from "src/views/Docuclipper/ExternalFilter";
import { createAlert } from "src/redux/reducers/Alerts";

// Add these type definitions
interface SankeyNode extends d3.SimulationNodeDatum {
  name: string;
  category: string;
  x0?: number;
  x1?: number;
  y0?: number;
  y1?: number;
}

interface SankeyLink {
  source: any;
  target: any;
  value: number;
}

let source;
let destination;
export function FlowOfFunds() {
  // const { isAdmin } = useIsAdmin()
  const isAdmin = false;

  const ref = useRef<any>(null);

  const externalFilterColumns = useMemo(
    () => [
      // {
      //   field: "account",
      //   label: "Account",
      //   filter: true,
      //   filterType: "text",
      //   defaultFilterType: "contains",
      // },
      {
        field: "date",
        label: "Date",
        filter: true,
        filterType: "date",
        defaultFilterType: "equals",
      },
      {
        field: "description",
        label: "Description",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
      },
      {
        field: "amount",
        label: "Amount",
        filter: true,
        filterType: "number",
        defaultFilterType: "equals",
      },
    ],
    []
  );

  const externalFilterChanged = useCallback((s, d) => {
    source = s;
    destination = d;
    (ref as any).current.api.onFilterChanged();
  }, []);

  // const transactions = useTransactions();

  const {
    selectedDocumentId,
    tls,
    selectedChunk,
    accountChunkLabels,
    pagination,
    refreshKey,
  } = useSelector((state: ReduxState) => state.TransactionManager);

  const gridApi = React.useRef<any>(null);
  const [isGridReady, setIsGridReady] = useState(false);

  const onGridReady = (params) => {
    gridApi.current = params.api;
    setIsGridReady(true);
    params.api.sizeColumnsToFit();
  };
  const dispatch = useDispatch();

  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    undefined
  );
  const [selectedAccount, setSelectedAccount] = useState<string | undefined>(
    undefined
  );
  const [tx1Account, setTx1Account] = useState<string | undefined>(undefined);
  const [tx2Account, setTx2Account] = useState<string | undefined>(undefined);
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  // Force grid refresh when filters change
  useEffect(() => {
    if (gridApi.current?.api && isGridReady) {
      // Trigger filter change to refresh the grid
      gridApi.current.api.onFilterChanged();
    }
  }, [selectedCategory, selectedAccount, tx1Account, tx2Account, isGridReady]);

  useEffect(() => {
    if (!gridApi.current || !isGridReady) {
      // console.log("exit 0 - grid not ready");
      return;
      return;
    }

    const dataSource: IDatasource = {
      rowCount: undefined,
      getRows: (params) => {
        const convertFilterModel = (): TableFilterConfig[] => {
          // TODO use params.filterModel
          return Object.entries(params.filterModel).map(
            ([field, filter]: [string, any]) => {
              // Get the actual filter type from the filter object
              const filterType = filter.filterType || "text"; // Default to 'text' if filterType is not specified
              const operationType = filter.type; // This is the operation (equals, contains, lessThan, etc.)

              // Initialize the filter object
              const filterOutput = {
                field,
                operator: operationType, // The operation (equals, contains, lessThan, etc.)
                valueFrom: undefined,
                valueTo: undefined,
                value: undefined,
              };

              if (filterType === "number") {
                // Handle number filters
                if (operationType === "inRange") {
                  filterOutput.valueFrom = filter.filter; // For range filters
                  filterOutput.valueTo = filter.filterTo;
                } else if (
                  operationType === "blank" ||
                  operationType === "notBlank"
                ) {
                  filterOutput.value = undefined; // No specific value for blank/not blank
                } else {
                  filterOutput.value = filter.filter; // Single value filters
                }
              } else if (filterType === "date") {
                // Handle date filters
                if (operationType === "inRange") {
                  filterOutput.valueFrom = filter.dateFrom; // Date filters use dateFrom
                  filterOutput.valueTo = filter.dateTo; // and dateTo
                } else if (
                  operationType === "blank" ||
                  operationType === "notBlank"
                ) {
                  filterOutput.value = undefined; // No specific value for blank/not blank
                } else {
                  filterOutput.value = filter.dateFrom; // Single value date filters use dateFrom
                }
              } else {
                // Handle text filters and other types
                filterOutput.value = filter.filter; // Default case
              }

              return filterOutput;
            }
          );
        };

        const convertSortModel = (sortModel: any[]) => {
          return sortModel.map((sort) => ({
            field: sort.colId,
            direction: sort.sort.toUpperCase(),
          }));
        };

        dispatch(
          fetchTls(false, {
            pagination: {
              page: Math.floor(params.startRow / pagination.pageSize) + 1,
              pageSize: params.endRow - params.startRow,
            },
            filters: [
              // Base filters
              // ...(selectedDocumentId
              //   ? [
              //       {
              //         field: "documentId",
              //         operator: "equals" as const,
              //         value: selectedDocumentId,
              //       },
              //     ]
              //   : []),
              // ...(selectedAccount
              //   ? [
              //       {
              //         field: "account",
              //         operator: "equals" as const,
              //         value: selectedAccount,
              //       },
              //     ]
              //   : []),
              // ...(selectedChunk
              //   ? [
              //       {
              //         field: "chunk",
              //         operator: "equals" as const,
              //         value: selectedChunk,
              //       },
              //     ]
              //   : []),
              {
                field: "included",
                operator: "equals" as const,
                value: true,
              },
              // Dynamic filters from AG Grid
              ...convertFilterModel(),
              // Add new filters
              ...(selectedCategory
                ? [
                    {
                      field: "category",
                      operator: "equals" as const,
                      value: selectedCategory,
                    },
                  ]
                : []),
              ...(selectedAccount
                ? [
                    {
                      field: "account",
                      operator: "equals" as const,
                      value: selectedAccount,
                    },
                  ]
                : []),
              ...(tx1Account
                ? [
                    {
                      field: "tx1Account",
                      operator: "equals" as const,
                      value: tx1Account,
                    },
                  ]
                : []),
              ...(tx2Account
                ? [
                    {
                      field: "tx2Account",
                      operator: "equals" as const,
                      value: tx2Account,
                    },
                  ]
                : []),
            ],
            sorting: convertSortModel(params.sortModel),
            successCallback: params.successCallback,
            accountChunkLabels,
            documents,
            hasQbo,
            job,
            l1,
            l2,
          })
        );
      },
    };

    if (gridApi.current && dataSource) {
      gridApi.current.setDatasource(dataSource);
    } else {
      console.error("Grid API or dataSource is not defined");
    }
  }, [
    dispatch,
    pagination.pageSize,
    selectedDocumentId,
    selectedAccount,
    selectedChunk,
    refreshKey,
    isGridReady,
    selectedCategory,
    tx1Account,
    tx2Account,
    l1,
    l2,
  ]);
  const { documents } = tls;
  const hasQbo = useHasQbo();

  const { matches } = useSelector(
    (state: ReduxState) => state.MatchedTransactions
  );

  React.useEffect(() => {
    if (matches.length === 0) {
      dispatch(fetchMatchedTransactions());
    }
  }, []);

  // console.log({ matches });
  const matchedIds = flatten(
    (matches || [])
      .filter(
        (x) => x?.match?.status === "approved" && Array.isArray(x?.transactions)
      )
      .map((x) => x.transactions)
  )
    .filter((x) => x && x.id)
    .map((x) => x.id);

  const defaultColDef = useMemo(() => {
    return {
      resizable: true,
    };
  }, []);

  const [columnDefs] = useState([
    {
      field: "account",
    },
    {
      field: "date",
      filter: "agDateColumnFilter",
      filterParams: datefilterParamsYYYYMMDD,
      sortable: true,
    },
    {
      field: "description",
      filter: "agTextColumnFilter",
      filterParams: {
        // pass in additional parameters to the Text Filter
      },
    },
    {
      field: "amount",
      filter: "agNumberColumnFilter",
      sortable: true,
      filterParams: {
        // pass in additional parameters to the Number Filter
      },
    },
    { field: "category", filter: "agSetColumnFilter" },
    // {
    //   headerName: "Is Transfer",
    //   field: "id",
    //   width: 120,
    //   cellRenderer: (params) => {
    //     if (!params.data) {
    //       return "";
    //     }
    //     const isTransfer = matchedIds.includes(params?.data?.id);
    //     // const isTransfer =
    //     return (
    //       <FontAwesomeIcon
    //         icon={isTransfer ? faCheck : faTimes}
    //         color={isTransfer ? "#2ecc71" : "#e74c3c"}
    //       />
    //     );
    //   },
    //   filter: "agSetColumnFilter",
    //   valueGetter: (params) => matchedIds.includes(params?.data?.id),
    // },
  ]);
  const onBtnExport = useCallback(() => {
    if (ref && ref.current) {
      (ref.current as any).api.exportDataAsCsv();
    }
  }, []);
  const { job } = useSelector((state: ReduxState) => state.JobData);

  interface SankeyData {
    nodes: Array<{ name: string; category: string; type: any }>;
    links: Array<{ source: number; target: number; value: number }>;
  }

  const [data2, setData2] = React.useState<SankeyData>({
    nodes: [],
    links: [],
  });
  const [isLoadingFlowData, setIsLoadingFlowData] = React.useState(false);
  React.useEffect(() => {
    if (!(job && job.id) && !(l1 && l2)) {
      return;
    }
    setIsLoadingFlowData(true);
    const fn =
      job && job.id ? getFlowOfFunds(job.id) : getFlowOfFundsTagLevel(l1, l2);
    fn.then((data) => {
      const reorganizedData = reorganizeNodes(data);
      setData2(reorganizedData);
      setIsLoadingFlowData(false);
    }).catch((err) => {
      setIsLoadingFlowData(false);
      dispatch(
        createAlert({
          message: err.message || "Error getting flow of funds",
          timeout: 0,
          type: "error",
        })
      );
    });
  }, [job, l1, l2]);

  const reorganizeNodes = (data) => {
    // Add position information to nodes based on category
    const orderedNodes = data.nodes.map((node) => ({
      ...node,
      x:
        node.category === "income"
          ? 20
          : node.category === "expense"
          ? 840
          : 400,
    }));

    // Create index mapping
    const nodeIndexMap = Object.fromEntries(
      orderedNodes.map((node, index) => [node.name, index])
    );

    // Update links
    const updatedLinks = data.links.map((link) => ({
      ...link,
      source: nodeIndexMap[data.nodes[link.source].name],
      target: nodeIndexMap[data.nodes[link.target].name],
    }));

    return { nodes: orderedNodes, links: updatedLinks };
  };

  // const [source, setSource] = React.useState();
  // const [destination, setDestination] = React.useState();

  // Show loading state while data is being fetched
  if (isLoadingFlowData) {
    return (
      <div className="my-2 alert alert-info" role="alert">
        Loading flow of funds data...
      </div>
    );
  }

  // Show message only when data has loaded but is empty
  if ((data2.links || []).length === 0 || (data2.nodes || []).length === 0) {
    return (
      <div className="my-2 alert alert-warning" role="alert">
        Please categorize your transactions first to see the flow of funds
        visualization.
      </div>
    );
  }

  function filterFn(rowNode) {
    if (!source || !destination) {
      return true;
    }

    const amount = parseFloat(rowNode.data.amount);
    // console.log("Filtering:", {
    //   id: rowNode.data.id,
    //   account: rowNode.data.account,
    //   category: rowNode.data.category,
    //   amount,
    //   source,
    //   destination,
    //   isMatched: matchedIds.includes(rowNode.data.id),
    // });

    // Income link clicked (from specific income category to account)
    if (data2.nodes.find((n) => n.name === source)?.category === "income") {
      const matches =
        !matchedIds.includes(rowNode.data.id) && // Not a matched transaction
        rowNode.data.account === destination && // Must be to the destination account
        amount > 0 && // Must be positive (income)
        `Income:${rowNode.data.category}` === source; // Must match the specific income category name
      // console.log("Income filter result:", matches);
      return matches;
    }

    // Expense link clicked (from account to specific expense category)
    if (
      data2.nodes.find((n) => n.name === destination)?.category === "expense"
    ) {
      const matches =
        !matchedIds.includes(rowNode.data.id) && // Not a matched transaction
        rowNode.data.account === source && // Must be from the source account
        amount < 0 && // Must be negative (expense)
        `Expense:${rowNode.data.category}` === destination; // Must match the specific expense category name
      // console.log("Expense filter result:", matches);
      return matches;
    }

    // Account to account transfer clicked

    // TODO don't ues matches to filter account to account, use source and destination

    // console.log({ matches });
    if (matchedIds.includes(rowNode.data.id)) {
      const match = matches.find((x) =>
        x.transactions.some((t) => t.id === rowNode.data.id)
      );

      if (match) {
        const [tx1, tx2] = match.transactions;
        const isValidTransfer =
          (tx1.account === source && tx2.account === destination) ||
          (tx1.account === destination && tx2.account === source);
        // console.log("Transfer filter result:", isValidTransfer);
        return isValidTransfer;
      }
    }

    return false;
  }

  const D3Sankey = ({ data }) => {
    return (
      <SizeMe>
        {({ size }) => (
          <div style={{ width: "100%", height: "500px" }}>
            <SankeyChart data={data} width={size.width || 0} height={500} />
          </div>
        )}
      </SizeMe>
    );
  };

  const SankeyChart = ({ data, width = 1040, height = 500 }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const chartGroupRef = useRef<SVGGElement>(null);
    const zoomBehaviorRef = useRef<d3.ZoomBehavior<
      SVGSVGElement,
      unknown
    > | null>(null);
    const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
    const [tooltip, setTooltip] = useState({
      show: false,
      content: "",
      x: 0,
      y: 0,
    });
    const [sankeyNodes, setSankeyNodes] = useState<any[]>([]);
    const [sankeyLinks, setSankeyLinks] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);

    // Compute sankey result - must be before any early returns
    const sankeyResult = useMemo(() => {
      if (!data || !data.nodes || !data.links) {
        return null;
      }

      try {
        const formatValue = (value: number) =>
          `$${Math.abs(value || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;

        // Pre-process nodes to assign columns and IDs
        // Calculate node counts once for consistent column assignment
        const accountNodes =
          data.nodes.filter((n) => n.category === "account") || [];
        const incomeNodes =
          data.nodes.filter((n) => n.category === "income") || [];
        const expenseNodes =
          data.nodes.filter((n) => n.category === "expense") || [];

        // Determine column layout:
        // - Income: column 0 (leftmost)
        // - Accounts: columns 1 to accountNodes.length (if income exists) OR 0 to accountNodes.length - 1 (if no income)
        // - Expenses: rightmost column (after all accounts)
        const hasIncome = incomeNodes.length > 0;
        const accountStartColumn = hasIncome ? 1 : 0;
        const expenseColumn = hasIncome
          ? accountNodes.length + 1 // Accounts occupy columns 1 to accountNodes.length
          : accountNodes.length; // Accounts occupy columns 0 to accountNodes.length - 1

        // Log column layout calculation
        console.log("=== Flow of Funds Column Assignment ===");
        console.log("Node counts:", {
          income: incomeNodes.length,
          accounts: accountNodes.length,
          expenses: expenseNodes.length,
          total: data.nodes.length,
        });
        console.log("Column layout:", {
          hasIncome,
          accountStartColumn,
          expenseColumn,
          accountColumns:
            accountNodes.length > 0
              ? `${accountStartColumn} to ${
                  accountStartColumn + accountNodes.length - 1
                }`
              : "none",
        });
        console.log(
          "Account nodes:",
          accountNodes.map((n) => n.name)
        );
        console.log(
          "Income nodes:",
          incomeNodes.map((n) => n.name)
        );
        console.log(
          "Expense nodes:",
          expenseNodes.map((n) => n.name)
        );

        const processedData = {
          nodes: (data.nodes || []).map((node) => {
            let column;
            switch (node.category) {
              case "income":
                // Income always goes in the leftmost column (0)
                column = 0;
                break;
              case "account":
                // Accounts go in the middle columns
                const accountIndex = accountNodes.findIndex(
                  (n) => n.name === node.name
                );
                if (accountIndex === -1) {
                  // Fallback if node not found (shouldn't happen, but safety check)
                  console.warn(
                    `Account node not found in accountNodes: ${node.name}`
                  );
                  column = accountStartColumn;
                } else {
                  column = accountStartColumn + accountIndex;
                }
                break;
              case "expense":
                // Expenses always go in the rightmost column
                column = expenseColumn;
                break;
              default:
                column = 0;
            }

            const assignedNode = {
              ...node,
              id: node.name,
              column,
            };

            console.log(
              `Node assigned: "${node.name}" (${node.category}) → Column ${column}`
            );

            return assignedNode;
          }),
          links: (data.links || [])
            .map((link) => {
              const sourceNode = data.nodes[link.source]?.name
                ? data.nodes[link.source]
                : data.nodes.find((n) => n.name === link.source);
              const targetNode = data.nodes[link.target]?.name
                ? data.nodes[link.target]
                : data.nodes.find((n) => n.name === link.target);

              if (!sourceNode || !targetNode) {
                return null;
              }

              return {
                ...link,
                source: link.source?.name || link.source,
                target: link.target?.name || link.target,
              };
            })
            .filter(Boolean),
        };

        // Log summary grouped by column
        const nodesByColumn = processedData.nodes.reduce((acc, node) => {
          if (!acc[node.column]) {
            acc[node.column] = [];
          }
          acc[node.column].push({
            name: node.name,
            category: node.category,
          });
          return acc;
        }, {} as Record<number, Array<{ name: string; category: string }>>);

        console.log("=== Column Summary ===");
        const sortedColumns = Object.keys(nodesByColumn)
          .map(Number)
          .sort((a, b) => a - b);
        sortedColumns.forEach((col) => {
          const nodes = nodesByColumn[col];
          console.log(
            `Column ${col}:`,
            nodes.map((n) => `${n.name} (${n.category})`).join(", ")
          );
        });
        console.log("======================");

        if (!processedData.nodes.length || !processedData.links.length) {
          return null;
        }

        // Function to detect circular links
        const detectCircularLinks = (nodes: any[], links: any[]) => {
          const visited = new Set();
          const recursionStack = new Set();

          const hasCycle = (nodeId: string): boolean => {
            if (recursionStack.has(nodeId)) return true;
            if (visited.has(nodeId)) return false;

            visited.add(nodeId);
            recursionStack.add(nodeId);

            const outgoingLinks = links.filter(
              (link) => link.source === nodeId
            );
            for (const link of outgoingLinks) {
              if (hasCycle(link.target)) return true;
            }

            recursionStack.delete(nodeId);
            return false;
          };

          for (const node of nodes) {
            if (!visited.has(node.id) && hasCycle(node.id)) return true;
          }
          return false;
        };

        // Function to remove circular links
        const removeCircularLinks = (nodes: any[], links: any[]) => {
          const validLinks: any[] = [];

          const wouldCreateCycle = (
            sourceId: string,
            targetId: string,
            currentLinks: any[]
          ): boolean => {
            const tempLinks = [
              ...currentLinks,
              { source: sourceId, target: targetId },
            ];
            const hasCycle = (nodeId: string, path: Set<string>): boolean => {
              if (path.has(nodeId)) return true;
              path.add(nodeId);
              const outgoingLinks = tempLinks.filter(
                (link) => link.source === nodeId
              );
              for (const link of outgoingLinks) {
                if (hasCycle(link.target, new Set(path))) return true;
              }
              return false;
            };
            return hasCycle(sourceId, new Set());
          };

          for (const link of links) {
            if (!wouldCreateCycle(link.source, link.target, validLinks)) {
              validLinks.push(link);
            }
          }
          return validLinks;
        };

        let finalLinks = processedData.links;
        if (detectCircularLinks(processedData.nodes, processedData.links)) {
          finalLinks = removeCircularLinks(
            processedData.nodes,
            processedData.links
          );
        }

        if (finalLinks.length === 0) {
          return { error: "noLinks" };
        }

        const finalProcessedData = {
          ...processedData,
          links: finalLinks,
        };

        const sankeyGenerator = sankey<SankeyNode, SankeyLink>()
          .nodeWidth(15)
          .nodePadding(10)
          .extent([
            [1, 5],
            [width - 1, height - 5],
          ])
          .nodeAlign((node: any) => node.column);

        const sankeyResult = sankeyGenerator(finalProcessedData);

        // Create a lookup map from node name/id to column for reliable access
        const nodeColumnMap = new Map(
          processedData.nodes.map((n) => [n.id || n.name, n.column])
        );

        // Manually enforce column positions by setting x0 and x1
        // Calculate the number of columns and column width
        const maxColumn = Math.max(...processedData.nodes.map((n) => n.column));
        const columnCount = maxColumn + 1;
        const columnWidth = (width - 2) / Math.max(1, columnCount - 1); // -2 for margins, -1 because we have columnCount positions

        console.log("=== Enforcing Column Positions ===");
        console.log(
          "Max column:",
          maxColumn,
          "Column count:",
          columnCount,
          "Column width:",
          columnWidth
        );

        // Override x positions for each node based on its column
        sankeyResult.nodes.forEach((node: any) => {
          // Get column from node property or lookup map
          const originalColumn =
            node.column ?? nodeColumnMap.get(node.id || node.name) ?? 0;

          // Calculate x position: column 0 starts at left margin, last column at right margin
          const xPosition =
            columnCount === 1
              ? width / 2 // Single column: center it
              : 1 + originalColumn * columnWidth; // Multiple columns: distribute evenly

          const nodeWidth = 15; // Same as nodeWidth in sankey generator
          node.x0 = xPosition - nodeWidth / 2;
          node.x1 = xPosition + nodeWidth / 2;

          // Preserve column for reference
          node.column = originalColumn;

          console.log(
            `Node "${
              node.name || node.id
            }" (column ${originalColumn}): x0=${node.x0.toFixed(
              1
            )}, x1=${node.x1.toFixed(1)}`
          );
        });

        // Update link paths to reflect new node positions
        // The sankey library should handle this, but we'll ensure links are updated
        sankeyResult.links.forEach((link: any) => {
          // Links should automatically update when nodes move, but we can force recalculation
          // by ensuring the link's path uses the updated node positions
          if (link.source && link.target) {
            // The link path is calculated by sankeyLinkHorizontal, which reads from source/target x/y positions
            // So as long as we've updated the node positions, the links should be correct
          }
        });

        console.log("=================================");

        return sankeyResult;
      } catch (err) {
        return { error: "generationError" };
      }
    }, [data, width, height]);

    // Update state when sankey result changes
    useEffect(() => {
      if (sankeyResult && !sankeyResult.error) {
        setSankeyNodes(sankeyResult.nodes);
        setSankeyLinks(sankeyResult.links);
        setError(null);
      } else if (sankeyResult?.error === "noLinks") {
        setSankeyNodes([]);
        setSankeyLinks([]);
        setError("noLinks");
      } else if (sankeyResult?.error === "generationError") {
        setSankeyNodes([]);
        setSankeyLinks([]);
        setError("generationError");
      }
    }, [sankeyResult]);

    // Set up zoom behavior - must be before any early returns
    useEffect(() => {
      if (!svgRef.current || !chartGroupRef.current || sankeyNodes.length === 0)
        return;

      const svg = d3.select(svgRef.current);
      const chartGroup = d3.select(chartGroupRef.current);

      const zoomBehavior = d3
        .zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.1, 4]) // Allow zoom from 10% to 400%
        .on("start", () => {
          if (svgRef.current) {
            svgRef.current.style.cursor = "grabbing";
          }
        })
        .on("zoom", (event) => {
          const { x, y, k } = event.transform;
          setTransform({ x, y, k });
          chartGroup.attr("transform", `translate(${x},${y}) scale(${k})`);
        })
        .on("end", () => {
          if (svgRef.current) {
            svgRef.current.style.cursor = "grab";
          }
        });

      // Store zoom behavior in ref so button functions can use it
      zoomBehaviorRef.current = zoomBehavior;
      svg.call(zoomBehavior);

      // Cleanup
      return () => {
        svg.on(".zoom", null);
      };
    }, [sankeyNodes, sankeyLinks, width, height]);

    // Early returns for error cases - after all hooks
    if (!data || !data.nodes || !data.links) {
      console.warn("Invalid data provided to SankeyChart");
      return null;
    }

    if (error === "noLinks") {
      return (
        <div className="alert alert-warning" role="alert">
          No valid flow connections found after removing circular references.
          This may indicate complex circular transaction patterns in your data.
          Please review your transaction categorization.
        </div>
      );
    }

    if (error === "generationError") {
      return (
        <div className="alert alert-danger" role="alert">
          Error generating flow diagram.
        </div>
      );
    }

    if (sankeyNodes.length === 0 || sankeyLinks.length === 0) {
      return null;
    }

    const formatValue = (value: number) =>
      `$${Math.abs(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

    // Use sankeyNodes and sankeyLinks from state for rendering
    const nodes = sankeyNodes;
    const links = sankeyLinks;

    // Reset zoom function
    const resetZoom = () => {
      if (!svgRef.current || !zoomBehaviorRef.current) return;
      const svg = d3.select(svgRef.current);
      svg
        .transition()
        .duration(750)
        .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
    };

    // Zoom in/out functions
    const zoomIn = () => {
      if (!svgRef.current || !zoomBehaviorRef.current) return;
      const svg = d3.select(svgRef.current);
      svg.transition().duration(200).call(zoomBehaviorRef.current.scaleBy, 1.5);
    };

    const zoomOut = () => {
      if (!svgRef.current || !zoomBehaviorRef.current) return;
      const svg = d3.select(svgRef.current);
      svg
        .transition()
        .duration(200)
        .call(zoomBehaviorRef.current.scaleBy, 1 / 1.5);
    };

    // After sankey generation, log the processed links
    // console.log(
    //   "Processed Links:",
    //   links.map((link) => ({
    //     source: {
    //       name: link.source.name,
    //       category: link.source.category,
    //       column: link.source.column,
    //     },
    //     target: {
    //       name: link.target.name,
    //       category: link.target.category,
    //       column: link.target.column,
    //     },
    //     value: link.value,
    //     path: `${link.source.category}/${link.source.name} → ${link.target.category}/${link.target.name}`,
    //   }))
    // );

    const getLinkColor = (link) => {
      const sourceNode = link.source;
      const targetNode = link.target;

      if (targetNode.category === "expense") return "#e74c3c"; // Red for expenses
      if (sourceNode.category === "income") return "#2ecc71"; // Green for income
      return "#3498db"; // Blue for transfers between accounts
    };

    const customLinkHorizontal = (link: any) => {
      const source = link.source;
      const target = link.target;

      // // For expense nodes, adjust the path to connect from account to expense
      // if (target.category === "expense") {
      //   return `
      //     M${source.x1},${source.y0}
      //     C${source.x1 + (target.x0 - source.x1) / 2},${source.y0}
      //      ${source.x1 + (target.x0 - source.x1) / 2},${target.y0}
      //      ${target.x0},${target.y0}
      //   `;
      // }

      // Default sankey path for other links
      return sankeyLinkHorizontal()(link);
    };

    return (
      <div style={{ position: "relative" }}>
        {/* Instructions */}
        <div
          style={{
            marginBottom: "10px",
            padding: "10px",
            backgroundColor: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            fontSize: "14px",
            color: "#495057",
          }}
        >
          <strong>How to use:</strong>
          <ul style={{ marginBottom: 0, paddingLeft: "20px" }}>
            <li>
              <strong>Zoom/Pan:</strong> Use the zoom controls (top right) or
              scroll to zoom, click and drag to pan
            </li>
            <li>
              <strong>Filter by category:</strong> Click on any link (income or
              expense) to filter transactions by that category
            </li>
            <li>
              <strong>Filter by account:</strong> Click on any account node to
              filter transactions for that account
            </li>
          </ul>
        </div>
        {/* Zoom Controls */}
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 10,
            display: "flex",
            gap: "5px",
            background: "white",
            padding: "5px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Button
            variant="light"
            size="sm"
            onClick={zoomIn}
            title="Zoom In (or scroll wheel)"
            style={{ padding: "4px 8px" }}
          >
            <FontAwesomeIcon icon="search-plus" />
          </Button>
          <Button
            variant="light"
            size="sm"
            onClick={zoomOut}
            title="Zoom Out (or scroll wheel)"
            style={{ padding: "4px 8px" }}
          >
            <FontAwesomeIcon icon="search-minus" />
          </Button>
          <Button
            variant="light"
            size="sm"
            onClick={resetZoom}
            title="Reset Zoom"
            style={{ padding: "4px 8px" }}
          >
            <FontAwesomeIcon icon="undo" />
          </Button>
        </div>

        <svg
          ref={svgRef}
          width={width}
          height={height}
          style={{ cursor: "grab", border: "1px solid #ddd" }}
        >
          <g ref={chartGroupRef} style={{ mixBlendMode: "multiply" }}>
            {/* Links with tooltips */}
            {links.map((link, i) => (
              <g
                key={i}
                onClick={() => {
                  // externalFilterChanged(link.source.name, link.target.name);
                  const sourceNode = data2.nodes.filter(
                    (x) => x.name === link.source.name
                  );
                  const targetNode = data2.nodes.filter(
                    (x) => x.name === link.target.name
                  );
                  // console.log({
                  //   source: link.source.name,
                  //   target: link.target.name,
                  //   nodes: data2.nodes,
                  //   sourceNode,
                  //   targetNode,
                  // });

                  if (sourceNode.length > 0 && targetNode.length > 0) {
                    if (
                      sourceNode[0].type === "accounts" &&
                      targetNode[0].type === "accounts"
                    ) {
                      // Account to account transfer
                      setSelectedAccount(undefined);
                      setSelectedCategory(undefined);
                      setTx1Account(sourceNode[0].name);
                      setTx2Account(targetNode[0].name);
                    } else if (
                      sourceNode[0].type === "categories" &&
                      targetNode[0].type === "accounts"
                    ) {
                      // Income category to account (income link)
                      // Only filter by income category, not by account
                      const categoryName =
                        (sourceNode[0].name.split(":")[1] || "").trim() ||
                        "Uncategorized";
                      // Clear other filters first
                      setSelectedAccount(undefined);
                      setTx1Account(undefined);
                      setTx2Account(undefined);
                      // Set category - always trigger refresh even if value is the same
                      setSelectedCategory(categoryName);
                      // Force grid refresh after state update completes
                      // Use double requestAnimationFrame to ensure React state has updated
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                          if (gridApi.current?.api) {
                            gridApi.current.api.onFilterChanged();
                          }
                        });
                      });
                    } else if (
                      sourceNode[0].type === "accounts" &&
                      targetNode[0].type === "categories"
                    ) {
                      // Account to expense category (expense link)
                      // Only filter by expense category, not by account
                      const categoryName =
                        (targetNode[0].name.split(":")[1] || "").trim() ||
                        "Uncategorized";
                      setSelectedCategory(categoryName);
                      setSelectedAccount(undefined); // Don't filter by account for expense links
                      setTx1Account(undefined);
                      setTx2Account(undefined);
                    } else {
                      setSelectedAccount(undefined);
                      setSelectedCategory(undefined);
                      setTx1Account(undefined);
                      setTx2Account(undefined);
                    }
                  }
                }}
                style={{ cursor: "pointer" }}
                onMouseEnter={(e) => {
                  setTooltip({
                    show: true,
                    content: `${link.source.name} → ${
                      link.target.name
                    }\nAmount: ${formatValue(
                      link.value
                    )}\nClick to view transactions`,
                    x: e.clientX,
                    y: e.clientY,
                  });
                }}
                onMouseLeave={() => setTooltip({ ...tooltip, show: false })}
              >
                <path
                  d={customLinkHorizontal(link)}
                  style={{
                    fill: "none",
                    strokeOpacity: 0.5,
                    stroke: getLinkColor(link),
                    strokeWidth: Math.max(1, link.width),
                  }}
                />
              </g>
            ))}

            {/* Nodes with tooltips */}
            {nodes.map((node, i) => (
              <g
                key={i}
                onClick={() => {
                  if (node.category === "income") {
                    externalFilterChanged("Income", node.name);
                  } else if (node.category === "expense") {
                    externalFilterChanged(node.name, "Expenses");
                  }
                  // console.log({
                  //   node,
                  // });
                  if (node.type === "categories") {
                    const categoryName =
                      (node.name.split(":")[1] || "").trim() || "Uncategorized";
                    setSelectedCategory(categoryName);
                    setSelectedAccount(undefined);
                  } else if (node.type === "accounts") {
                    setSelectedCategory(undefined);
                    setSelectedAccount(node.name);
                  }
                  setTx1Account(undefined);
                  setTx2Account(undefined);
                }}
                style={{ cursor: "pointer" }}
                onMouseEnter={(e) => {
                  setTooltip({
                    show: true,
                    content: `${node.name} (${
                      node.category
                    })\nTotal Flow: ${formatValue(
                      node.value
                    )}\nInflow: ${formatValue(
                      node.targetLinks?.reduce(
                        (sum, link) => sum + link.value,
                        0
                      ) || 0
                    )}\nOutflow: ${formatValue(
                      node.sourceLinks?.reduce(
                        (sum, link) => sum + link.value,
                        0
                      ) || 0
                    )}\nClick to view transactions`,
                    x: e.clientX,
                    y: e.clientY,
                  });
                }}
                onMouseLeave={() => setTooltip({ ...tooltip, show: false })}
              >
                <rect
                  x={node.x0}
                  y={node.y0}
                  width={node.x1 - node.x0}
                  height={node.y1 - node.y0}
                  fill="#666"
                  opacity={0.8}
                />
                <text
                  x={
                    node.category === "expense"
                      ? node.x0 - 6
                      : node.x0 < width / 2
                      ? node.x1 + 6
                      : node.x0 - 6
                  }
                  y={(node.y1 + node.y0) / 2}
                  dy="0.35em"
                  textAnchor={
                    node.category === "expense"
                      ? "end"
                      : node.x0 < width / 2
                      ? "start"
                      : "end"
                  }
                  fontSize={12}
                  style={{ fill: "black" }}
                >
                  {node.name}
                  <tspan
                    x={
                      node.category === "expense"
                        ? node.x0 - 6
                        : node.x0 < width / 2
                        ? node.x1 + 6
                        : node.x0 - 6
                    }
                    dy="1.2em"
                  >
                    {formatValue(Math.abs(node.value))}
                  </tspan>
                </text>
              </g>
            ))}
          </g>
        </svg>

        {tooltip.show && (
          <div
            style={{
              position: "absolute",
              top: tooltip.y,
              left: tooltip.x,
              background: "rgba(0, 0, 0, 0.8)",
              color: "white",
              padding: "8px",
              borderRadius: "4px",
              fontSize: "12px",
              pointerEvents: "none",
              whiteSpace: "pre-line",
              transform: "translate(10px, -50%)",
              zIndex: 1000,
            }}
          >
            {tooltip.content}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="sankey-charts">
      <div style={{ width: "100%" }}>
        <D3Sankey data={data2} />
      </div>

      <div className="h3 mt-4">Transactions</div>

      <Row>
        {/* <Col>
          {isAdmin && (
            <Button variant="link" onClick={onBtnExport}>
              <FontAwesomeIcon icon="download" />
              <span className="ml-1">Export as CSV</span>
            </Button>
          )}
        </Col> */}

        <Col>
          Filter by
          <ExternalFilter
            columnDefs={externalFilterColumns}
            gridApi={gridApi.current}
          />
          <Row className="align-items-center">
            <div className="filter-info ml-3">
              {(selectedAccount ||
                selectedCategory ||
                tx1Account ||
                tx2Account) && <span>Filter: </span>}
              {selectedAccount && selectedCategory && (
                <span>
                  Account: {selectedAccount}, Category: {selectedCategory}
                </span>
              )}
              {selectedAccount && !selectedCategory && (
                <span>Account: {selectedAccount}</span>
              )}
              {selectedCategory && !selectedAccount && (
                <span>Category: {selectedCategory}</span>
              )}
              {tx1Account && tx2Account && (
                <span>
                  Transfer: {tx1Account} ↔ {tx2Account}
                </span>
              )}
            </div>
            {(selectedAccount ||
              selectedCategory ||
              tx1Account ||
              tx2Account) && (
              <Button
                variant="link"
                onClick={() => {
                  // externalFilterChanged(undefined, undefined);
                  setSelectedCategory(undefined);
                  setSelectedAccount(undefined);
                  setTx1Account(undefined);
                  setTx2Account(undefined);
                }}
              >
                <FontAwesomeIcon icon="trash" />
                <span className="ml-1">Clear Filters</span>
              </Button>
            )}
          </Row>
        </Col>
      </Row>

      <Row>
        <div className="ag-theme-alpine" style={{ height: 600, width: "100%" }}>
          <AgGridReact
            singleClickEdit
            defaultColDef={defaultColDef}
            ref={ref as any}
            // rowData={rowData}
            columnDefs={columnDefs}
            isExternalFilterPresent={() => true}
            doesExternalFilterPass={filterFn}
            pagination={true}
            paginationPageSize={pagination.pageSize}
            rowModelType={"infinite"}
            cacheBlockSize={pagination.pageSize}
            cacheOverflowSize={2}
            maxConcurrentDatasourceRequests={1}
            infiniteInitialRowCount={1}
            maxBlocksInCache={2}
            rowData={undefined}
            onGridReady={onGridReady}
          ></AgGridReact>
        </div>
      </Row>
      {/* {isAdmin && (
        <Row>
          <SupersetDash
            dashboardName={"flowOfFunds"}
            height={"600px"}
            width={"100%"}
            reload={true} // change dinamically to reload the dashboard
          />
        </Row>
      )} */}
    </div>
  );
}

export default FlowOfFunds;
